import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Save, Plus, FileText, Trash2, ChevronUp, ChevronDown, Grip } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AIActorInquiryForm() {
  const { actorId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    inquiry_form_fields: []
  });

  useEffect(() => {
    fetchActorData();
  }, [actorId]);

  const fetchActorData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/ai/actors/${actorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const actor = response.data;
      setFormData({
        inquiry_form_fields: actor.inquiry_form_fields || [
          { id: "name", type: "text", label: "Name", placeholder: "Your name", required: true, validation: "", options: "" },
          { id: "email", type: "email", label: "Email", placeholder: "your@email.com", required: true, validation: "", options: "" },
          { id: "phone", type: "tel", label: "Phone", placeholder: "Your phone number", required: false, validation: "", options: "" },
          { id: "message", type: "textarea", label: "Message", placeholder: "Your message", required: true, validation: "", options: "" }
        ]
      });
    } catch (error) {
      toast.error("Failed to fetch actor data");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem("token");
      
      // CRITICAL: Fetch current actor data to avoid overwriting other fields
      const currentResponse = await axios.get(`${API}/ai/actors/${actorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const currentActor = currentResponse.data;
      
      // Merge current actor data with our inquiry form changes
      const mergedData = {
        ...currentActor,                                    // Keep all existing fields
        inquiry_form_fields: formData.inquiry_form_fields   // Update only inquiry form fields
      };
      
      // Save merged data
      await axios.put(`${API}/ai/actors/${actorId}`, mergedData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Inquiry form updated successfully");
    } catch (error) {
      toast.error("Failed to update inquiry form");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading inquiry form...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/ai/actors/manage/${actorId}`)}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Manage
        </Button>
        <h1 className="text-2xl font-bold">Inquiry Form Builder</h1>
        <Button onClick={handleSave} disabled={saving}>
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      {/* Inquiry Form Builder Content */}
                {/* Inquiry Form Builder - Enhanced */}
                <Card className="border-2 border-green-200 bg-green-50/30 mt-6">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-green-900">
                      <FileText className="w-5 h-5" />
                      Inquiry Form Builder
                    </CardTitle>
                    <CardDescription>
                      Customize the fields shown on the inquiry form on the public profile. Drag to reorder fields.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Current Form Fields */}
                    <div className="space-y-3">
                      {formData.inquiry_form_fields && formData.inquiry_form_fields.map((field, index) => (
                        <Card key={field.id} className="border-green-200">
                          <CardContent className="p-4">
                            <div className="flex items-start gap-4">
                              {/* Drag Handle & Reorder Buttons */}
                              <div className="flex flex-col items-center gap-1 pt-1">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  disabled={index === 0}
                                  onClick={() => {
                                    if (index > 0) {
                                      const newFields = [...formData.inquiry_form_fields];
                                      [newFields[index], newFields[index - 1]] = [newFields[index - 1], newFields[index]];
                                      setFormData({...formData, inquiry_form_fields: newFields});
                                    }
                                  }}
                                  className="h-6 w-6 p-0"
                                >
                                  <ChevronUp className="w-4 h-4" />
                                </Button>
                                <Grip className="w-4 h-4 text-muted-foreground" />
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  disabled={index === formData.inquiry_form_fields.length - 1}
                                  onClick={() => {
                                    if (index < formData.inquiry_form_fields.length - 1) {
                                      const newFields = [...formData.inquiry_form_fields];
                                      [newFields[index], newFields[index + 1]] = [newFields[index + 1], newFields[index]];
                                      setFormData({...formData, inquiry_form_fields: newFields});
                                    }
                                  }}
                                  className="h-6 w-6 p-0"
                                >
                                  <ChevronDown className="w-4 h-4" />
                                </Button>
                              </div>

                              {/* Field Configuration */}
                              <div className="flex-1 space-y-3">
                                {/* Row 1: Field ID and Type */}
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <Label className="text-xs">Field ID</Label>
                                    <Input
                                      value={field.id}
                                      onChange={(e) => {
                                        const newFields = [...formData.inquiry_form_fields];
                                        newFields[index].id = e.target.value;
                                        setFormData({...formData, inquiry_form_fields: newFields});
                                      }}
                                      placeholder="field_name"
                                      className="text-sm"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-xs">Field Type</Label>
                                    <Select
                                      value={field.type}
                                      onValueChange={(value) => {
                                        const newFields = [...formData.inquiry_form_fields];
                                        newFields[index].type = value;
                                        setFormData({...formData, inquiry_form_fields: newFields});
                                      }}
                                    >
                                      <SelectTrigger className="text-sm">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="text">Text</SelectItem>
                                        <SelectItem value="email">Email</SelectItem>
                                        <SelectItem value="tel">Phone</SelectItem>
                                        <SelectItem value="textarea">Textarea</SelectItem>
                                        <SelectItem value="number">Number</SelectItem>
                                        <SelectItem value="url">URL</SelectItem>
                                        <SelectItem value="select">Select Dropdown</SelectItem>
                                        <SelectItem value="checkbox">Checkbox</SelectItem>
                                        <SelectItem value="radio">Radio Buttons</SelectItem>
                                        <SelectItem value="file">File Upload</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                </div>
                                
                                {/* Row 2: Label and Placeholder */}
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <Label className="text-xs">Label</Label>
                                    <Input
                                      value={field.label}
                                      onChange={(e) => {
                                        const newFields = [...formData.inquiry_form_fields];
                                        newFields[index].label = e.target.value;
                                        setFormData({...formData, inquiry_form_fields: newFields});
                                      }}
                                      placeholder="Field Label"
                                      className="text-sm"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-xs">Placeholder</Label>
                                    <Input
                                      value={field.placeholder || ''}
                                      onChange={(e) => {
                                        const newFields = [...formData.inquiry_form_fields];
                                        newFields[index].placeholder = e.target.value;
                                        setFormData({...formData, inquiry_form_fields: newFields});
                                      }}
                                      placeholder="Enter placeholder text"
                                      className="text-sm"
                                    />
                                  </div>
                                </div>

                                {/* Row 3: Help Text (Description) */}
                                <div>
                                  <Label className="text-xs">Help Text (Optional)</Label>
                                  <Input
                                    value={field.helpText || ''}
                                    onChange={(e) => {
                                      const newFields = [...formData.inquiry_form_fields];
                                      newFields[index].helpText = e.target.value;
                                      setFormData({...formData, inquiry_form_fields: newFields});
                                    }}
                                    placeholder="Additional help text for the user"
                                    className="text-sm"
                                  />
                                </div>

                                {/* Conditional: Options for Select, Radio, Checkbox */}
                                {(field.type === 'select' || field.type === 'radio' || field.type === 'checkbox') && (
                                  <div>
                                    <Label className="text-xs">
                                      {field.type === 'checkbox' ? 'Options (comma-separated, for multiple checkboxes)' : 'Options (comma-separated)'}
                                    </Label>
                                    <Input
                                      value={field.options ? field.options.join(', ') : ''}
                                      onChange={(e) => {
                                        const newFields = [...formData.inquiry_form_fields];
                                        newFields[index].options = e.target.value.split(',').map(o => o.trim()).filter(o => o);
                                        setFormData({...formData, inquiry_form_fields: newFields});
                                      }}
                                      placeholder={field.type === 'checkbox' ? 'Leave empty for single yes/no checkbox' : 'Option 1, Option 2, Option 3'}
                                      className="text-sm"
                                    />
                                  </div>
                                )}

                                {/* Conditional: File Upload Settings */}
                                {field.type === 'file' && (
                                  <div className="grid grid-cols-2 gap-3">
                                    <div>
                                      <Label className="text-xs">Accepted File Types</Label>
                                      <Input
                                        value={field.acceptedFileTypes || ''}
                                        onChange={(e) => {
                                          const newFields = [...formData.inquiry_form_fields];
                                          newFields[index].acceptedFileTypes = e.target.value;
                                          setFormData({...formData, inquiry_form_fields: newFields});
                                        }}
                                        placeholder=".pdf,.doc,.docx"
                                        className="text-sm"
                                      />
                                    </div>
                                    <div>
                                      <Label className="text-xs">Max File Size (MB)</Label>
                                      <Input
                                        type="number"
                                        value={field.maxFileSize || '5'}
                                        onChange={(e) => {
                                          const newFields = [...formData.inquiry_form_fields];
                                          newFields[index].maxFileSize = e.target.value;
                                          setFormData({...formData, inquiry_form_fields: newFields});
                                        }}
                                        placeholder="5"
                                        className="text-sm"
                                      />
                                    </div>
                                  </div>
                                )}

                                {/* Row 4: Validation Settings */}
                                {(field.type === 'text' || field.type === 'textarea' || field.type === 'number') && (
                                  <div className="grid grid-cols-2 gap-3">
                                    <div>
                                      <Label className="text-xs">Min Length / Min Value</Label>
                                      <Input
                                        type="number"
                                        value={field.minLength || ''}
                                        onChange={(e) => {
                                          const newFields = [...formData.inquiry_form_fields];
                                          newFields[index].minLength = e.target.value;
                                          setFormData({...formData, inquiry_form_fields: newFields});
                                        }}
                                        placeholder="Optional"
                                        className="text-sm"
                                      />
                                    </div>
                                    <div>
                                      <Label className="text-xs">Max Length / Max Value</Label>
                                      <Input
                                        type="number"
                                        value={field.maxLength || ''}
                                        onChange={(e) => {
                                          const newFields = [...formData.inquiry_form_fields];
                                          newFields[index].maxLength = e.target.value;
                                          setFormData({...formData, inquiry_form_fields: newFields});
                                        }}
                                        placeholder="Optional"
                                        className="text-sm"
                                      />
                                    </div>
                                  </div>
                                )}

                                {/* Row 5: Required Switch */}
                                <div className="flex items-center gap-2">
                                  <Switch
                                    checked={field.required}
                                    onCheckedChange={(checked) => {
                                      const newFields = [...formData.inquiry_form_fields];
                                      newFields[index].required = checked;
                                      setFormData({...formData, inquiry_form_fields: newFields});
                                    }}
                                  />
                                  <Label className="text-xs">Required Field</Label>
                                </div>
                              </div>

                              {/* Delete Button */}
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const newFields = formData.inquiry_form_fields.filter((_, i) => i !== index);
                                  setFormData({...formData, inquiry_form_fields: newFields});
                                }}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>

                    {/* Add Field Button */}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        const newField = {
                          id: `field_${Date.now()}`,
                          label: "New Field",
                          type: "text",
                          required: false,
                          placeholder: "",
                          helpText: ""
                        };
                        setFormData({
                          ...formData,
                          inquiry_form_fields: [...(formData.inquiry_form_fields || []), newField]
                        });
                      }}
                      className="w-full border-green-200 text-green-700 hover:bg-green-50"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Form Field
                    </Button>

                    {/* Tips */}
                    <div className="p-3 bg-green-100 rounded border border-green-200">
                      <p className="text-xs text-green-800">
                        <strong>Tips:</strong><br/>
                        • Field IDs should be unique and use lowercase with underscores (e.g., company_name, project_budget)<br/>
                        • Use reorder buttons to change field sequence<br/>
                        • For checkboxes: leave options empty for a single yes/no checkbox, or add multiple options for checkbox group<br/>
                        • File uploads are stored temporarily and links will be included in submission data
                      </p>
                    </div>
                  </CardContent>
                </Card>

    </div>
  );
}
